import React, { useEffect, useRef, useState } from "react";
import CellSaveStatusIndicator from "../../controls/CellSaveStatusIndicator";
import OrderPricingNumberInput from "./OrderPricingNumberInput";
import type { DiscountType, PricingMutationResult, PricingSaveStatus, SourceItemPricingChoice, SummaryDiscountPayload } from "./summaryPricingTypes";

type DiscountSummary =
  | { kind: "none" | "mixed"; type: null; value: "" }
  | { kind: "uniform"; type: DiscountType; value: string };

type SummaryDiscountInputProps = {
  groupKey: string;
  productLabel: string;
  sourceItems?: SourceItemPricingChoice[];
  disabled?: boolean;
  busy?: boolean;
  onCommit?: (payload: SummaryDiscountPayload) => Promise<PricingMutationResult | undefined>;
};

const VALUE_PATTERN = /^\d+(\.\d{1,4})?$/;
const MAX_VALUE = 9999999999.9999;

function summaryFor(items: SourceItemPricingChoice[] = []): DiscountSummary {
  const types = items.map((item) => item.discount_type || null);
  if (!types.length || types.every((type) => type === null)) {
    return { kind: "none", type: null, value: "" };
  }

  if (types.every((type) => type === "dollar")) {
    const total = items.reduce((sum, item) => sum + Number(item.discount_value || 0), 0);
    return { kind: "uniform", type: "dollar", value: String(Number(total.toFixed(4))) };
  }

  if (types.every((type) => type === "percentage")) {
    const values = items.map((item) => Number(item.discount_value));
    if (values.every(Number.isFinite) && values.every((value) => value === values[0])) {
      return { kind: "uniform", type: "percentage", value: String(items[0].discount_value ?? values[0]) };
    }
  }

  return { kind: "mixed", type: null, value: "" };
}

function snapshotSignature(items: SourceItemPricingChoice[] = []) {
  return JSON.stringify(items.map((item) => [item.id, item.discount_type ?? null, item.discount_value ?? null]));
}

export default function SummaryDiscountInput({
  groupKey,
  productLabel,
  sourceItems = [],
  disabled = false,
  busy = false,
  onCommit,
}: SummaryDiscountInputProps) {
  const summary = summaryFor(sourceItems);
  const signature = snapshotSignature(sourceItems);
  const [mode, setMode] = useState<DiscountType>(() => summary.type || "dollar");
  const [value, setValue] = useState(() => summary.value);
  const [dirty, setDirty] = useState(false);
  const [status, setStatus] = useState<PricingSaveStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const pending = useRef(false);
  const skipBlur = useRef(false);
  const cancelingDraft = useRef(false);
  const inputId = `summary-discount-${groupKey.replace(/[^a-zA-Z0-9_-]/g, "-")}`;

  useEffect(() => {
    if (!dirty && !pending.current) {
      const next = summaryFor(sourceItems);
      setMode(next.type || "dollar");
      setValue(next.value);
    }
  }, [sourceItems, signature, dirty]);

  useEffect(() => {
    if (status !== "saved") return;
    const timeout = setTimeout(() => setStatus("idle"), 3000);
    return () => clearTimeout(timeout);
  }, [status]);

  const save = async (nextMode: DiscountType, nextValue: string) => {
    if (pending.current || disabled || busy || !onCommit || sourceItems.length === 0) return;
    if (nextValue.trim() !== "" && (!VALUE_PATTERN.test(nextValue.trim()) || !Number.isFinite(Number(nextValue)) || Number(nextValue) > MAX_VALUE)) {
      setDirty(true);
      setStatus("error");
      setErrorMessage("Enter a non-negative value with up to four decimal places.");
      return;
    }

    const payload: SummaryDiscountPayload = {
      source_item_ids: sourceItems.map((item) => item.id),
      discount_type: nextValue.trim() === "" ? null : nextMode,
      discount_value: nextValue.trim() === "" ? null : Number(nextValue),
      expected_items: sourceItems.map((item) => ({
        id: item.id,
        discount_type: item.discount_type ?? null,
        discount_value: item.discount_value ?? null,
      })),
    };
    pending.current = true;
    setStatus("saving");
    setErrorMessage("");
    try {
      const result = await onCommit(payload);
      if (!result?.success) {
        setStatus("error");
        setErrorMessage(result?.data?.message || "Could not save the row discount. Your entry is kept; retry.");
        setDirty(true);
        return;
      }
      setDirty(false);
      setMode(nextMode);
      setValue(nextValue.trim());
      setStatus("saved");
    } catch {
      setStatus("error");
      setErrorMessage("Could not save the row discount. Your entry is kept; retry.");
      setDirty(true);
    } finally {
      pending.current = false;
    }
  };

  const switchMode = () => {
    const nextMode = mode === "dollar" ? "percentage" : "dollar";
    if (pending.current || disabled || busy) return;
    setMode(nextMode);
    if (value.trim() === "") {
      setStatus("idle");
      setErrorMessage("");
      return;
    }
    setDirty(true);
    save(nextMode, value);
  };

  if (!sourceItems.length) return <span aria-label={`No active items for ${productLabel}`}>—</span>;
  if (disabled) {
    if (summary.kind === "none") return <span>—</span>;
    if (summary.kind === "mixed") return <span className="text-xs text-gray-600">Mixed</span>;
    return <span className="whitespace-nowrap tabular-nums">{summary.type === "percentage" ? `${summary.value}%` : `$${Number(summary.value).toFixed(2)}`}</span>;
  }

  const retry = () => save(mode, value);

  return (
    <div className="min-w-40 space-y-1">
      <div className="flex min-h-11 items-center gap-1 whitespace-nowrap md:min-h-9">
        <button
          type="button"
          data-discount-mode-toggle="true"
          aria-label={`Switch ${productLabel} discount to ${mode === "dollar" ? "percentage" : "dollar"}`}
          disabled={pending.current || busy}
          onClick={switchMode}
          className="min-h-11 min-w-11 border-0 bg-transparent p-0 font-semibold text-indigo-700 hover:underline focus-visible:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 disabled:opacity-60 md:min-h-9 md:min-w-9"
        >{mode === "dollar" ? "$" : "%"}</button>
        <OrderPricingNumberInput
          ariaLabel={`Discount ${mode === "dollar" ? "amount in dollars for" : "percentage for"} ${productLabel}`}
          ariaDescribedBy={`${inputId}-status`}
          ariaInvalid={status === "error" || undefined}
          disabled={pending.current || busy}
          id={inputId}
          mode="decimal"
          minFractionDigits={0}
          maxFractionDigits={4}
          onChange={(nextValue) => {
            if (cancelingDraft.current) return;
            setValue(nextValue);
            setDirty(true);
            setStatus("idle");
            setErrorMessage("");
          }}
          onBlur={(event) => {
            if (event.relatedTarget instanceof HTMLElement && event.relatedTarget.dataset.discountModeToggle) return;
            if (skipBlur.current) {
              skipBlur.current = false;
              setTimeout(() => { cancelingDraft.current = false; }, 0);
              return;
            }
            if (dirty) save(mode, value);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              if (dirty) save(mode, value);
            } else if (event.key === "Escape") {
              event.preventDefault();
              skipBlur.current = true;
              cancelingDraft.current = true;
              const next = summaryFor(sourceItems);
              setMode(next.type || "dollar");
              setValue(next.value);
              setDirty(false);
              setStatus("idle");
              setErrorMessage("");
              event.currentTarget.blur();
            }
          }}
          placeholder={summary.kind === "mixed" ? "Replace mixed" : undefined}
          value={value}
        />
        {summary.kind === "mixed" ? <span className="ml-1 text-xs text-gray-600">Mixed</span> : null}
        <CellSaveStatusIndicator status={status} />
      </div>
      <p id={`${inputId}-status`} role={status === "error" ? "alert" : "status"} className={`min-h-4 text-xs ${status === "error" ? "text-red-700" : "text-gray-600"}`}>
        {errorMessage}
      </p>
      {status === "error" ? <button type="button" onClick={retry} disabled={pending.current || busy} className="min-h-11 text-xs text-indigo-700 underline md:min-h-8">Retry discount save</button> : null}
    </div>
  );
}
